import { AuthTokens } from "@/model/AuthTokens";
import {
  BloodGlucoseObservationsDto,
  toBloodGlucoseObservations,
} from "@/model/BloodGlucoseObservations";
import BloodGlucoseThresholds from "@/model/BloodGlucoseThresholds";
import {
  BloodPressureObservationsDto,
  toBloodPressureObservations,
} from "@/model/BloodPressureObservations";
import BloodPressureThresholds from "@/model/BloodPressureThresholds";
import { CaregiverApiDto, toCaregiver } from "@/model/Caregiver";
import { ClinicalNoteApiDto, CreateClinicalNoteApiDto, toClinicalNote } from "@/model/ClinicalNote";
import { Jwt } from "@/model/Jwt";
import { LoginCredentials } from "@/model/LoginCredentials";
import { LoginResponse } from "@/model/LoginResponse";
import { OrganizationApiDto } from "@/model/Organization";
import { PatientOffboardingReason } from "@/model/Patient";
import { PatientApiDto } from "@/model/PatientApiDto";
import { Instant } from "@js-joda/core";
import merge from "lodash/merge";
import { authTokens, setAuthTokens } from "./store";

const backendUrl = import.meta.env.VITE_BACKEND_API_URL as string;
const appVersion = import.meta.env.VITE_APP_VERSION as string;

export class HttpError extends Error {
  statusCode: number;
  responseJson?: unknown;

  constructor(statusCode: number, url: string, responseJson?: unknown) {
    super(`HttpError: ${statusCode} ${url} ${JSON.stringify(responseJson)}`);
    this.statusCode = statusCode;
    this.responseJson = responseJson;
  }
}

export const jsonFetch = async <T>(url: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    headers: {
      "Content-Type": "application/json",
      "X-App-Version": appVersion,
    },
  };

  const response = await fetch(url, merge(defaultOptions, options));
  const responseBody = await response.text();
  const responseJson = responseBody ? JSON.parse(responseBody) : undefined;

  if (!response.ok) {
    throw new HttpError(response.status, url, responseJson);
  }

  return responseJson as T;
};

export const authenticatedFetch = async <T>(url: string, options: RequestInit = {}) => {
  const { accessJwt, refreshJwt } = authTokens()!;
  const accessJwtExpiration = Instant.parse(accessJwt.expiration);
  const isAboutToExpire = Instant.now().isAfter(accessJwtExpiration.minusSeconds(150)); // User access tokens should stay valid for 15 minutes, we refresh them 2m30s min advance to be sure that it doesn't expire during a long running request.
  const refreshJwtString = refreshJwt.token;
  if (isAboutToExpire) {
    await refreshTokens(refreshJwtString);
  }
  try {
    return await _authenticatedFetch<T>({ url, accessJwt, options });
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 401) {
      await refreshTokens(refreshJwtString);
      return await _authenticatedFetch<T>({ url, accessJwt, options });
    }
    throw error;
  }
};

const _authenticatedFetch = async <T>({
  url,
  accessJwt,
  options = {},
}: {
  url: string;
  accessJwt: Jwt;
  options?: RequestInit;
}) => {
  const defaultOptions = {
    headers: {
      Authorization: `Bearer ${accessJwt.token}`,
    },
  };
  return await jsonFetch<T>(url, merge(defaultOptions, options));
};

export const logInWithEmailAndPassword = async ({ email, password }: LoginCredentials) => {
  const { accessJwt, refreshJwt } = await jsonFetch<LoginResponse>(`${backendUrl}/v1/login`, {
    method: "POST",
    body: JSON.stringify({
      email: email,
      password: password,
    }),
  });
  const authTokens = { accessJwt, refreshJwt };
  setAuthTokens(authTokens);
  return authTokens;
};

export const logOut = () => {
  jsonFetch(`${backendUrl}/v1/logout`, {
    method: "POST",
    body: JSON.stringify({
      refreshToken: authTokens()!.refreshJwt.token,
    }),
  }).catch(() => {}); // Run the logout request to the backend, but don't wait for it or handle the error
  setAuthTokens(null);
};

let refreshingTokensPromise: Promise<AuthTokens> | null = null;

const refreshTokens = async (refreshToken: string) => {
  const actuallyRefreshTokens = async () => {
    try {
      const { accessJwt, refreshJwt } = await jsonFetch<LoginResponse>(
        `${backendUrl}/v1/token/refresh`,
        {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        }
      );
      const authTokens = { accessJwt, refreshJwt };
      setAuthTokens(authTokens);
      return authTokens;
    } catch (error) {
      // BioT can say LOGIN_FAILED even if the token hasn't yet expired based on time, probably some other invalidation logic on their end.
      if (error instanceof HttpError && error.statusCode === 403) {
        setAuthTokens(null); // This will trigger a redirect to the login page
      }
      throw error;
    }
  };

  if (!refreshingTokensPromise) {
    refreshingTokensPromise = actuallyRefreshTokens().finally(() => {
      refreshingTokensPromise = null;
    });
  }

  return refreshingTokensPromise;
};

export type ResendInvitationEmailParams = {
  email: string;
};

export const resendInvitationEmail = ({ email }: ResendInvitationEmailParams) =>
  jsonFetch(`${backendUrl}/v1/invitation/resend`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export type CheckSignUpInvitationParams = {
  token: string;
  userId: string;
};

export const checkSignUpInvitation = async ({ token, userId }: CheckSignUpInvitationParams) => {
  await jsonFetch(`${backendUrl}/v1/invitation/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  });
};

export type SignUpParams = {
  token: string;
  userId: string;
  password: string;
};

export const signUp = async ({ token, userId, password }: SignUpParams) => {
  const { accessJwt, refreshJwt } = await jsonFetch<LoginResponse>(
    `${backendUrl}/v1/invitation/accept`,
    {
      method: "POST",
      body: JSON.stringify({ token, userId, password }),
    }
  );
  const authTokens = { accessJwt, refreshJwt };
  setAuthTokens(authTokens);
  return authTokens;
};

export type SendForgotPasswordEmailParams = {
  email: string;
};

export const sendForgotPasswordEmail = ({ email }: SendForgotPasswordEmailParams) =>
  jsonFetch(`${backendUrl}/v1/password/forgot`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export type CheckResetPasswordTokenParams = {
  token: string;
  userId: string;
};

export const checkResetPasswordToken = async ({ token, userId }: CheckResetPasswordTokenParams) => {
  await jsonFetch(`${backendUrl}/v1/password/reset/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  });
};

export type ResetPasswordParams = {
  token: string;
  userId: string;
  password: string;
};

export const resetPassword = async ({ token, userId, password }: ResetPasswordParams) => {
  const { accessJwt, refreshJwt } = await jsonFetch<LoginResponse>(
    `${backendUrl}/v1/password/reset`,
    {
      method: "POST",
      body: JSON.stringify({ token, userId, password }),
    }
  );
  const authTokens = { accessJwt, refreshJwt };
  setAuthTokens(authTokens);
  return authTokens;
};

export const getSelfUser = async () => {
  const dto = await authenticatedFetch<CaregiverApiDto>(`${backendUrl}/v1/self`);
  return toCaregiver(dto);
};

export const getPatientsForOrganization = async (organizationId: string) =>
  await authenticatedFetch<PatientApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/patients`
  );

export type GetPatientOptions = {
  patientId: string;
  organizationId: string;
};

export const getPatient = async ({ patientId, organizationId }: GetPatientOptions) =>
  await authenticatedFetch<PatientApiDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}`
  );

export const getOrganization = (organizationId: string) =>
  authenticatedFetch<OrganizationApiDto>(`${backendUrl}/v1/organizations/${organizationId}`);

export type GetClinicalNotesOptions = {
  patientId: string;
  organizationId: string;
};

export const getClinicalNotes = async ({ patientId, organizationId }: GetClinicalNotesOptions) => {
  const dtos = await authenticatedFetch<ClinicalNoteApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/clinical-notes`
  );
  return dtos.map((dto) => toClinicalNote(dto));
};

export type AddClinicalNoteOptions = {
  patientId: string;
  organizationId: string;
  note: CreateClinicalNoteApiDto;
};

export const addClinicalNote = ({ patientId, organizationId, note }: AddClinicalNoteOptions) =>
  authenticatedFetch<ClinicalNoteApiDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/clinical-notes`,
    {
      method: "POST",
      body: JSON.stringify(note),
    }
  );

export type UpdateBloodGlucoseClinicalProtocolAndThresholdsOptions = {
  prescriptionId: string;
  clinicalProtocolId: string;
  thresholds: BloodGlucoseThresholds;
};

export const updateBloodGlucoseClinicalProtocolAndThresholds = async ({
  prescriptionId,
  clinicalProtocolId,
  thresholds,
}: UpdateBloodGlucoseClinicalProtocolAndThresholdsOptions) => {
  await authenticatedFetch(
    `${backendUrl}/v1/prescriptions/${prescriptionId}/clinical-protocol/diabetes`,
    {
      method: "PUT",
      body: JSON.stringify({
        clinicalProtocolId,
        ...thresholds,
      }),
    }
  );
};

export type UpdateBloodPressureClinicalProtocolOptions = {
  prescriptionId: string;
  clinicalProtocolId: string;
  thresholds: BloodPressureThresholds;
};

export const updateBloodPressureClinicalProtocol = async ({
  prescriptionId,
  clinicalProtocolId,
  thresholds,
}: UpdateBloodPressureClinicalProtocolOptions) => {
  await authenticatedFetch(
    `${backendUrl}/v1/prescriptions/${prescriptionId}/clinical-protocol/hypertension`,
    {
      method: "PUT",
      body: JSON.stringify({ clinicalProtocolId, ...thresholds }),
    }
  );
};

export type GetObservationsOptions = {
  patientId: string;
  organizationId: string;
  from: Instant;
  to: Instant;
};

export const getBloodGlucoseObservations = async ({
  patientId,
  organizationId,
  from,
  to,
}: GetObservationsOptions) => {
  const queryParams = new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  });
  const dto = await authenticatedFetch<BloodGlucoseObservationsDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/observations/blood-glucose?${queryParams}`
  );
  return toBloodGlucoseObservations(dto);
};

export const getBloodPressureObservations = async ({
  patientId,
  organizationId,
  from,
  to,
}: GetObservationsOptions) => {
  const queryParams = new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  });
  const dto = await authenticatedFetch<BloodPressureObservationsDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/observations/blood-pressure?${queryParams}`
  );
  return toBloodPressureObservations(dto);
};

export type OffboardPatientOptions = {
  prescriptionId: string;
  reason: PatientOffboardingReason;
  deliveryDate?: string;
  note?: string;
};

export const offboardPatient = async ({
  prescriptionId,
  reason,
  deliveryDate,
  note,
}: OffboardPatientOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/prescriptions/${prescriptionId}/offboard`, {
    method: "POST",
    body: JSON.stringify({
      reason,
      deliveryDate,
      note,
    }),
  });
};

export type UpdateDeliveryDateOptions = {
  patientId: string;
  deliveryDate: string | undefined;
};

export const updateDeliveryDate = async ({
  patientId,
  deliveryDate,
}: UpdateDeliveryDateOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/patients/${patientId}/delivery-date`, {
    method: "PUT",
    body: JSON.stringify({
      deliveryDate,
    }),
  });
};

export type GetPrescribingCaregiversOptions = {
  organizationId: string;
};

export const getPrescribingCaregivers = async ({
  organizationId,
}: GetPrescribingCaregiversOptions) => {
  const dtos = await authenticatedFetch<CaregiverApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/caregivers`
  );
  return dtos.map((dto) => toCaregiver(dto));
};
